// 会社別詳細（DEC-053 案B / S-055-2,3,4）
//
// 「会社」は **プロジェクト内ローカルの相手先サブグループ**（横断はしない）。
// ProjectListPage の社名カードタップで、単一 project + company を受け取って遷移する。
// 表示は ProjectDashboardPage と同等のリッチUI（タイムライン＋サマリ＋「この会社にAIで聞く」）を
// components/DashboardSections.tsx の共通部品で実現する。AI・サマリの対象は
// **そのプロジェクト内の当該会社に紐づく打ち合わせ群**（projectId + companyId フィルタ）。
//
// データ取得:
//   - meetings: 単一PJ内 `companyIds array-contains company.id`（未分類は全件→空配列クライアント絞り）
//   - 会社サマリ: projects/{projectId}.companyDashboards.{companyId}（computeProjectDashboard companyId 拡張）
import React, { useEffect, useMemo, useState } from 'react';
import { User } from 'firebase/auth';
import { collection, doc, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import {
  PlanType, planHasAiFeatures, planHasFullTimeline, webDisplayPlan,
} from '../plan';
import { useFreeTimelineViewLimit } from '../remoteConfig';
import { Btn, Card, Icon, Pill } from '../components/primitives';
import {
  Timeline, AISection, SummaryCard, Decisions, KeyPersons,
  DashboardDecision, DashboardKeyPerson, toDate,
} from '../components/DashboardSections';
import ChatPanel from '../components/ChatPanel';
import { Company, UNCLASSIFIED_ID } from '../useCompanies';

interface Project {
  id: string;
  name: string;
}

interface Meeting {
  id: string;
  title: string;
  recordedAt?: any;
  duration?: number;
  status?: string;
  deletedAt?: any;
  companyIds?: string[];
  tags?: string[];
  channel?: string;
  excerpt?: string;
  summary?: string;
  participants?: string[];
}

// projects/{id}.companyDashboards.{companyId}（computeProjectDashboard companyId 拡張の派生キャッシュ）
interface CompanyDashboardData {
  summaryText?: string;
  decisions?: DashboardDecision[];
  keyPersons?: DashboardKeyPerson[];
  lastComputedAt?: any;
  latestMeetingRecordedAt?: any;
  computedFromMeetingCount?: number;
  truncated?: boolean;
  stale?: boolean;
}

interface Props {
  user: User;
  project: Project;
  company: Company;
  onBack: () => void;
  onSelectMeeting: (meeting: Meeting) => void;
  plan: PlanType;
  planLoading: boolean;
  // Web で議事録の中身を閲覧できないプラン（free / light / DEC-059）。
  // タイムラインの会議カードのプレビュー本文だけをぼかす（タイトル・日時は読める）。
  webMinutesLocked?: boolean;
}

const CompanyTimelinePage: React.FC<Props> = ({ user, project, company, onBack, onSelectMeeting, plan, planLoading, webMinutesLocked }) => {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [companyDashboard, setCompanyDashboard] = useState<CompanyDashboardData | undefined>(undefined);
  const [chatOpen, setChatOpen] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeError, setRecomputeError] = useState<string | null>(null);

  const isUnclassified = company.id === UNCLASSIFIED_ID;
  // 未分類は AI/サマリの対象にしない（companyId フィルタが空集合になるため）。タイムラインのみ。
  const aiEligible = !isUnclassified;

  // ─── 会議タイムライン購読（単一PJ内・companyId フィルタ） ───
  useEffect(() => {
    setLoading(true);
    // 未分類は array-contains で引けない（空配列）ため全件取得してクライアント側で絞る。
    // 通常の相手先は companyIds array-contains × recordedAt 複合インデックスで引く。
    const base = collection(db, 'projects', project.id, 'meetings');
    const q = isUnclassified
      ? query(base, orderBy('recordedAt', 'desc'))
      : query(base, where('companyIds', 'array-contains', company.id), orderBy('recordedAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        let data = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() } as Meeting))
          .filter((m) => m.deletedAt == null);
        if (isUnclassified) {
          data = data.filter((m) => !Array.isArray(m.companyIds) || m.companyIds.length === 0);
        }
        setMeetings(data);
        setError(null);
        setLoading(false);
      },
      (e: any) => {
        console.error('[CompanyTimelinePage] failed to subscribe meetings:', e);
        const detail = e?.code ? `${e.code}: ${e.message ?? e}` : (e?.message ?? String(e));
        setError(`タイムラインの取得に失敗しました (${detail})`);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [project.id, company.id, isUnclassified]);

  // ─── 会社サマリ購読（projects/{id} doc の companyDashboards[companyId]） ───
  useEffect(() => {
    if (!aiEligible) { setCompanyDashboard(undefined); return; }
    const ref = doc(db, 'projects', project.id);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const all = (snap.data() as any).companyDashboards || {};
          setCompanyDashboard(all[company.id]);
        }
      },
      (e: any) => {
        console.error('[CompanyTimelinePage] failed to subscribe project doc:', e);
      }
    );
    return unsubscribe;
  }, [project.id, company.id, aiEligible]);

  // ─── プラン出し分け（ProjectDashboardPage と同条件） ───
  // 無料の閲覧件数は RemoteConfig で動的制御（既定2件 / DEC-049 更新）。
  const freeViewLimit = useFreeTimelineViewLimit();
  const aiUnlocked = planLoading || planHasAiFeatures(plan);
  // Web 表示では free を light と同一にする（webDisplayPlan）＝ free も全件表示（中身はモザイク）。
  const fullTimeline = planLoading || planHasFullTimeline(webDisplayPlan(plan));
  const hiddenMeetingCount = fullTimeline ? 0 : Math.max(0, meetings.length - freeViewLimit);
  const visibleMeetings = fullTimeline ? meetings : meetings.slice(0, freeViewLimit);

  const decisions = companyDashboard?.decisions ?? [];
  const keyPersons = companyDashboard?.keyPersons ?? [];
  const summaryText = companyDashboard?.summaryText ?? '';
  const hasDashboard = !!companyDashboard && (!!summaryText || decisions.length > 0 || keyPersons.length > 0);

  // stale 判定: その会社の最新会議 recordedAt > companyDashboard.lastComputedAt（ProjectDashboardPage と同型）。
  const stale = useMemo(() => {
    if (!hasDashboard) return false;
    if (companyDashboard?.stale === true) return true;
    const latest = toDate(meetings[0]?.recordedAt);
    const computedAt = toDate(companyDashboard?.lastComputedAt);
    if (!latest || !computedAt) return false;
    return latest.getTime() > computedAt.getTime();
  }, [hasDashboard, companyDashboard, meetings]);

  const goPricing = () => { window.location.href = '/pricing'; };

  const openChat = () => {
    if (!aiEligible) return;
    if (aiUnlocked) setChatOpen(true);
    else goPricing();
  };

  // 出典 meetingId → タイムラインの会議を開く。
  const jump = (meetingId?: string) => {
    if (!meetingId) return;
    const m = meetings.find((x) => x.id === meetingId);
    if (m) onSelectMeeting(m);
  };

  // オンデマンド再計算（standard 限定）。成功時は project doc 購読が companyDashboards を更新。
  const recompute = async () => {
    if (recomputing) return;
    setRecomputing(true);
    setRecomputeError(null);
    try {
      const fn = httpsCallable<{ projectId: string; companyId: string }, any>(functions, 'computeProjectDashboard');
      await fn({ projectId: project.id, companyId: company.id });
    } catch (e: any) {
      console.error('[CompanyTimelinePage] computeProjectDashboard failed:', e);
      const msg: string = e?.message || String(e);
      if (msg.includes('PLAN_REQUIRED_STANDARD')) {
        setRecomputeError('サマリの生成はスタンダードプランでご利用いただけます。');
      } else if (msg.includes('EMPTY_PROJECT')) {
        setRecomputeError('この会社の打ち合わせがまだありません。');
      } else {
        setRecomputeError(`サマリの最新化に失敗しました: ${msg}`);
      }
    } finally {
      setRecomputing(false);
    }
  };

  return (
    <div style={{ maxWidth: 1240, margin: '0 auto', padding: '20px 24px 96px' }}>
      {/* パンくず */}
      <button onClick={onBack} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
        color: 'var(--fg-tertiary)', font: 'var(--type-ui)', cursor: 'pointer', marginBottom: 16, padding: 0,
      }}>
        <Icon name="arrow-left" size={16} />一覧に戻る
      </button>

      {/* 会社ヘッダ（プロジェクト名 → 会社名 + kind + AIボタン） */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginBottom: 6 }}>
            {project.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ font: 'var(--type-h1)', color: 'var(--fg)' }}>
              {isUnclassified ? '未分類の打ち合わせ' : company.name}
            </h1>
            {!isUnclassified && company.kind && <Pill kind="neutral">{company.kind}</Pill>}
          </div>
          <p style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginTop: 6 }}>
            {meetings.length}件の打ち合わせ · 会社ごとに自動で整理されています
          </p>
        </div>
        {aiEligible && (
          <Btn kind="accent" icon={aiUnlocked ? 'message-square-text' : 'lock'} onClick={openChat}
            title={aiUnlocked ? '' : 'スタンダードで利用できます'}>
            この会社にAIで聞く
          </Btn>
        )}
      </div>

      {/* 2カラム（未分類は AI 列を出さず1カラム） */}
      <div className={aiEligible ? 'dash-grid' : undefined}>
        {/* 左: タイムライン */}
        <div>
          <Timeline
            meetings={visibleMeetings}
            loading={loading}
            error={error}
            totalCount={meetings.length}
            onOpenMeeting={onSelectMeeting}
            emptyTitle={isUnclassified ? 'この相手先の打ち合わせはまだありません' : 'この会社の打ち合わせはまだありません'}
            emptyHint="Androidアプリから録音してください"
            lockExcerpt={webMinutesLocked}
          />
          {/* free プランのロック導線（直近 freeViewLimit 件のみ・既定2 / DEC-059） */}
          {hiddenMeetingCount > 0 && (
            <div style={{ position: 'relative', marginTop: 14 }}>
              <Card pad={18} style={{ borderStyle: 'dashed' }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 999, flexShrink: 0, display: 'grid', placeItems: 'center',
                    background: 'var(--cta-fill)', color: 'var(--cta)',
                  }}>
                    <Icon name="lock" size={18} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ font: 'var(--type-ui)', color: 'var(--fg)', marginBottom: 6 }}>
                      これ以前の{hiddenMeetingCount}件はライト以上で閲覧できます
                    </div>
                    <p style={{ font: 'var(--type-caption)', color: 'var(--fg-secondary)', lineHeight: 1.6, marginBottom: 12 }}>
                      無料プランは直近{freeViewLimit}件まで。ライト（¥480/月）で全件、スタンダード（¥980/月）でAIにも聞けます。
                    </p>
                    <Btn kind="cta" size="sm" icon="arrow-up-circle" onClick={goPricing}>プランを見る</Btn>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>

        {/* 右: AI集約（会社サマリ / 決定事項 / キーパーソン）。未分類では非表示。 */}
        {aiEligible && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <AISection locked={!aiUnlocked} onUpsell={goPricing} message="スタンダードで会社サマリを自動生成します">
              <SummaryCard
                hasDashboard={hasDashboard}
                summaryText={summaryText}
                computedAt={companyDashboard?.lastComputedAt}
                stale={stale}
                recomputing={recomputing}
                recomputeError={recomputeError}
                onRecompute={recompute}
                locked={!aiUnlocked}
                title="会社サマリ"
                emptyText="この会社のサマリはまだ生成されていません。打ち合わせが揃ったら、この会社の議事録からサマリ・決定事項・キーパーソンを生成できます。"
              />
            </AISection>

            <AISection locked={!aiUnlocked} onUpsell={goPricing} message="スタンダードで決定事項を自動抽出します">
              <Decisions decisions={decisions} onJump={jump} />
            </AISection>

            <AISection locked={!aiUnlocked} onUpsell={goPricing} message="スタンダードでキーパーソンを整理します">
              <KeyPersons persons={keyPersons} />
            </AISection>
          </div>
        )}
      </div>

      {/* フローティングチャット起動ボタン（AI対象の会社のみ） */}
      {aiEligible && !chatOpen && (
        <button onClick={openChat} style={{
          position: 'fixed', right: 24, bottom: 24, zIndex: 55,
          height: 52, padding: '0 20px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
          background: 'var(--cta)', color: 'var(--on-cta)', border: 'none',
          display: 'flex', alignItems: 'center', gap: 9, font: 'var(--type-ui)', fontWeight: 600,
          boxShadow: '0 12px 32px var(--cta-glow)',
        }}>
          <Icon name={aiUnlocked ? 'message-square-text' : 'lock'} size={19} stroke={2} />
          AIに聞く
        </button>
      )}

      {aiEligible && (
        <ChatPanel
          open={chatOpen && aiUnlocked}
          project={{ id: project.id, name: company.name, meetingCount: meetings.length, companyId: company.id }}
          onClose={() => setChatOpen(false)}
          onJump={(mid) => { setChatOpen(false); jump(mid); }}
        />
      )}
    </div>
  );
};

export default CompanyTimelinePage;
